import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import {
  arrayOf, func, number, any, string, bool, oneOf,
} from 'prop-types';
import { InputControlLabel, IconButton } from '@picsio/ui';
import { Copy as CopyIcon, CheckIcon } from '@picsio/icons';
import DT from 'duration-time-conversion';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import TranscriptSearch from '../TranscriptSearch';
import TranscriptMenu from '../TranscriptMenu';
import { downloadSubFile } from '../../../../helpers/transcript';
import TranscriptItem from '../TranscriptItem';
import * as utils from '../../utils';
import Logger from '../../../../services/Logger';
import { deleteTranscript } from '../../../../store/actions/assets';
import CopyTranscriptDialog from '../CopyTranscriptDialog';
import { LocalStorage } from '../../../../shared/utils';
import Tooltip from '../../../Tooltip';
import l18n from '../../../../shared/strings';
import sdk from '../../../../sdk';
import Toast from '../../../Toast';

const COPY_FEEDBACK_TIMEOUT_MS = 2000;

function TranscriptList({
  videoCurrentTime,
  setVideoPlayerCurrentTime,
  transcript,
  assetId,
  activeRevisionID = null,
  assetName,
  manageTranscriptPermission,
  lang = 'en',
  transcribing = 'skipped',
}) {
  const dispatch = useDispatch();
  const [copied, setCopied] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isReaderView, setReaderView] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const copyTimeoutRef = useRef(null);

  const displayedTranscript = useMemo(() => {
    const baseData = isReaderView ? utils.mergeSpeakers(transcript) : transcript;
    return baseData.map((item) => ({
      start: item.start,
      end: item.end,
      text: utils.searchValueInText(item.text, searchValue),
      speaker: item.speaker,
    }));
  }, [isReaderView, transcript, searchValue]);

  useEffect(() => {
    const index = displayedTranscript.findIndex(
      (item) => DT.t2d(item.start) <= videoCurrentTime && DT.t2d(item.end) > videoCurrentTime,
    );
    if (index !== currentIndex) setCurrentIndex(index);
  }, [videoCurrentTime, displayedTranscript, currentIndex]);

  const handleCopy = useCallback(() => {
    Logger.log('User', 'TranscriptCopyTranscriptClicked');
    const dontShowAgain = LocalStorage.get('picsio.dontShowAgainCopyTranscriptDialog');
    if (dontShowAgain) {
      const isSpeakers = LocalStorage.get('picsio.includeSpeakerToCopyTranscript');
      const isTimestamps = LocalStorage.get('picsio.includeTimestampToCopyTranscript');
      utils.copyTranscript({ transcript, isSpeakers, isTimestamps });
      setCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, COPY_FEEDBACK_TIMEOUT_MS);
    } else {
      CopyTranscriptDialog({ transcript });
    }
  }, [transcript]);

  const handleDelete = useCallback(async () => {
    Logger.log('User', 'TranscriptDeleteClicked');
    try {
      setLoading(true);
      await sdk.assets.deleteTranscript(assetId, activeRevisionID);
      dispatch(deleteTranscript({ assetId, revisionId: activeRevisionID }));
      Toast(l18n.TRANSCRIPT.TOASTS.textTranscriptionDeleted, { type: 'success' });
    } catch (error) {
      Logger.log('User', 'TranscriptDeleteFailed', { error });
      Toast(l18n.TRANSCRIPT.TOASTS.textTranscriptionNotDeleted, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [dispatch, assetId, activeRevisionID]);

  const handleReaderMode = useCallback(() => {
    Logger.log('User', `TranscriptReaderMode${isReaderView ? 'Off' : 'On'}`);
    setReaderView(!isReaderView);
  }, [isReaderView]);

  const renderTranscriptItems = () => {
    let lastSpeaker = null;

    return displayedTranscript.map((subt, index) => {
      const isNewSpeaker = lastSpeaker !== subt.speaker;
      lastSpeaker = subt.speaker;

      if (Array.isArray(subt.text) && subt.text.length <= 1) return null;
      return (
        <TranscriptItem
          key={subt.start + subt.text}
          isActive={currentIndex === index}
          setVideoPlayerCurrentTime={setVideoPlayerCurrentTime}
          startTime={subt.start}
          text={subt.text}
          speaker={subt.speaker}
          assetId={assetId}
          activeRevisionID={activeRevisionID}
          manageTranscriptPermission={manageTranscriptPermission}
          transcribing={transcribing}
          isNewSpeaker={isNewSpeaker}
          isReaderView={isReaderView}
        />
      );
    });
  };

  return (
    <div className={cn('TranscriptList', isLoading && 'disabled')}>
      <div className="TranscriptList__search-bar">
        <InputControlLabel
          control="switch"
          label="Reader mode"
          value={isReaderView}
          onChange={handleReaderMode}
        />
        <div className="TranscriptList__search-controls">
          <TranscriptSearch setSearchValue={setSearchValue} />
          <Tooltip content={l18n.TRANSCRIPT.tooltipCopy(copied)} placement="top">
            <IconButton
              onClick={handleCopy}
              size="lg"
              className="fieldCopyToSettings"
              componentProps={{
                'data-testid': 'iconCopyTranscript',
              }}
            >
              <Choose>
                <When condition={copied}>
                  <CheckIcon className="checkIcon" />
                </When>
                <Otherwise>
                  <CopyIcon />
                </Otherwise>
              </Choose>
            </IconButton>
          </Tooltip>
          <TranscriptMenu
            isDownloadable
            assetId={assetId}
            lang={lang}
            setReaderView={() => {
              Logger.log('User', `TranscriptReaderMode${isReaderView ? 'Off' : 'On'}`);
              setReaderView(!isReaderView);
            }}
            onDelete={handleDelete}
            downloadSub={() => {
              Logger.log('User', 'TranscriptSRTDownloaded');
              downloadSubFile(transcript, assetName, 'srt');
            }}
            transcript={transcript}
          />
        </div>
      </div>

      <ul className="TranscriptList__list">
        {renderTranscriptItems()}
      </ul>
    </div>
  );
}

TranscriptList.propTypes = {
  setVideoPlayerCurrentTime: func.isRequired,
  videoCurrentTime: number.isRequired,
  transcript: arrayOf(any).isRequired,
  assetId: string.isRequired,
  activeRevisionID: string,
  assetName: string.isRequired,
  lang: string,
  manageTranscriptPermission: bool.isRequired,
  transcribing: oneOf(['skipped', 'waiting', 'running', 'complete', 'failed', 'rejected']),
};

export default TranscriptList;
